import { lensPath, lensProp, set } from "ramda";

import { parseDateToISO } from "widgets";

import type WebApiClient from "../../api/WebApiClient.js";
import { restrictionTypeOptions } from "../../ui-modules/core/SearchScopeSelector.js";
import { ADVANCED_SEARCH_FORM, AdvancedSearch } from "../../ui-modules/core/navbar/AdvancedSearch.js";
import { type IAppState } from "../reducer.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { updateUserParams } from "./auth.js";
import { parseSearchAttributeAndReplaceProjectTermWithSystemSetting } from "./config.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type ISelectOption } from "@/app-utils/form.ts";
import {
  CONSTRAINT_OPERATORS,
  type IRsqlComparisonExpr,
  type IRsqlFilterTree,
  type RsqlFilterConstraint,
  encodeFilter,
  isOperationUnit,
  parse,
} from "@/app-utils/rsql.ts";
import {
  type AdvancedSearchParams,
  type AdvancedSearchPreview,
  type InputAdvancedSearchInput,
  type SearchAttribute,
  type SearchScope,
  type SearchType,
} from "@/graphql.ts";
import {
  ADVANCED_SEARCH_ATTRIBUTES_REQUEST,
  ADVANCED_SEARCH_ATTRIBUTES_SUCCESS,
  ADVANCED_SEARCH_CLEAR,
  ADVANCED_SEARCH_CONFIG_FAILURE,
  ADVANCED_SEARCH_CONFIG_REQUEST,
  ADVANCED_SEARCH_CONFIG_SUCCESS,
  ADVANCED_SEARCH_POPULATE,
  ADVANCED_SEARCH_PREVIEW_CLEAR,
  ADVANCED_SEARCH_PREVIEW_FAILURE,
  ADVANCED_SEARCH_PREVIEW_REQUEST,
  ADVANCED_SEARCH_PREVIEW_SUCCESS,
  ADVANCED_SEARCH_SCOPE_OPTIONS_FAILURE,
  ADVANCED_SEARCH_SCOPE_OPTIONS_REQUEST,
  ADVANCED_SEARCH_SCOPE_OPTIONS_SUCCESS,
} from "@/redux/action-types.ts";
import { CELL_TYPE, ENTITY_TYPE, SAMPLE_FIELDS, SEARCH_RESTRICTION_TYPES } from "@/settings/settings.ts";

/**
 * State definition
 */
export interface IAdvancedSearchState {
  searchTypes: SearchType[];
  searchAttributes: SearchAttribute[];
  isConfigLoading: boolean;
  isAttributesLoading: boolean;
  isPreviewLoading: boolean; // flag informing about the search preview loading status
  searchPreview: AdvancedSearchPreview;
  lastSearch: InputAdvancedSearchInput;
  initialParams: AdvancedSearchParams;
}

export const initialState: IAdvancedSearchState = {
  searchTypes: null,
  searchAttributes: [],
  isConfigLoading: false,
  isAttributesLoading: false,
  isPreviewLoading: false,
  searchPreview: null,
  lastSearch: null,
  initialParams: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [ADVANCED_SEARCH_CONFIG_REQUEST]: (state: IAdvancedSearchState): IAdvancedSearchState => {
    return {
      ...state,
      isConfigLoading: true,
    };
  },
  [ADVANCED_SEARCH_CONFIG_SUCCESS]: (state: IAdvancedSearchState, searchTypes: SearchType[]): IAdvancedSearchState => {
    return {
      ...state,
      isConfigLoading: false,
      searchTypes,
    };
  },
  [ADVANCED_SEARCH_CONFIG_FAILURE]: (state: IAdvancedSearchState): IAdvancedSearchState => {
    return {
      ...state,
      isConfigLoading: false,
      isAttributesLoading: false,
      searchTypes: null,
      searchAttributes: [],
    };
  },
  [ADVANCED_SEARCH_ATTRIBUTES_REQUEST]: (state: IAdvancedSearchState): IAdvancedSearchState => {
    return {
      ...state,
      isAttributesLoading: true,
      searchAttributes: [],
    };
  },
  [ADVANCED_SEARCH_ATTRIBUTES_SUCCESS]: (
    state: IAdvancedSearchState,
    searchAttributes: SearchAttribute[],
  ): IAdvancedSearchState => {
    return {
      ...state,
      isAttributesLoading: false,
      searchAttributes,
    };
  },
  [ADVANCED_SEARCH_PREVIEW_REQUEST]: (
    state: IAdvancedSearchState,
    advancedSearchInput: InputAdvancedSearchInput,
  ): IAdvancedSearchState => ({
    ...state,
    isPreviewLoading: true,
    lastSearch: advancedSearchInput, // we store the search input so that we can match the result against the search response ID to avoid race issues, and make sure we don't search twice the exact same input
  }),
  [ADVANCED_SEARCH_PREVIEW_SUCCESS]: (
    state: IAdvancedSearchState,
    searchPreview: AdvancedSearchPreview,
  ): IAdvancedSearchState => {
    if (state.lastSearch && state.lastSearch.id === searchPreview.id) {
      // check against the stored searchPreview identifier
      return {
        ...state,
        isPreviewLoading: false,
        searchPreview: searchPreview,
      };
    }
    return state;
  },
  [ADVANCED_SEARCH_PREVIEW_FAILURE]: (
    state: IAdvancedSearchState,
    searchPreviewIdentifier: number,
  ): IAdvancedSearchState => {
    if (state.lastSearch && state.lastSearch.id === searchPreviewIdentifier) {
      // check against the stored searchPreview identifier
      return {
        ...state,
        isPreviewLoading: false,
      };
    }
    return state;
  },
  [ADVANCED_SEARCH_CLEAR]: (state: IAdvancedSearchState): IAdvancedSearchState => {
    return {
      ...state,
      isPreviewLoading: false,
      searchPreview: null,
      lastSearch: null,
      initialParams: null,
    };
  },
  [ADVANCED_SEARCH_PREVIEW_CLEAR]: (state: IAdvancedSearchState): IAdvancedSearchState => {
    return {
      ...state,
      isPreviewLoading: false,
      searchPreview: null,
      lastSearch: null,
    };
  },
  [ADVANCED_SEARCH_POPULATE]: (
    state: IAdvancedSearchState,
    initialParams: AdvancedSearchParams,
  ): IAdvancedSearchState => {
    return { ...state, initialParams };
  },
});

/**
 * Action creators
 */

export function clearAdvancedSearch() {
  return {
    type: ADVANCED_SEARCH_CLEAR,
  };
}

export function clearAdvancedSearchPreview() {
  return {
    type: ADVANCED_SEARCH_PREVIEW_CLEAR,
  };
}

export function populateAdvancedSearchPanel(initialParams: AdvancedSearchParams) {
  return {
    type: ADVANCED_SEARCH_POPULATE,
    payload: initialParams,
  };
}

export function loadAdvancedSearchTypes() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<SearchType[]> => {
    const advancedSearchState: IAdvancedSearchState = getState().advancedSearch;
    if (!advancedSearchState.searchTypes) {
      // this is one time config request, therefore if the data already exists no need to fetch it again

      dispatch({ type: ADVANCED_SEARCH_CONFIG_REQUEST });

      return webApiClient
        .fetchAdvancedSearchTypes()
        .then((advancedSearchTypes: SearchType[]) => {
          dispatch({
            type: ADVANCED_SEARCH_CONFIG_SUCCESS,
            payload: advancedSearchTypes,
          });
          return advancedSearchTypes;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: ADVANCED_SEARCH_CONFIG_FAILURE,
            alertTitle: "Fetching of Advanced Search Configuration Failed",
          }),
        );
    } else {
      return Promise.resolve(advancedSearchState.searchTypes);
    }
  };
}

export function loadAdvancedSearchAttributes(searchType: SearchType): ReduxAction<Promise<SearchAttribute[]>> {
  return (dispatch, _getState, webApiClient): Promise<SearchAttribute[]> => {
    dispatch({ type: ADVANCED_SEARCH_ATTRIBUTES_REQUEST });

    return webApiClient
      .fetchAdvancedSearchAttributes(searchType.entityType, searchType.subTypeId)
      .then((advancedSearchAttributes: SearchAttribute[]) => {
        dispatch({
          type: ADVANCED_SEARCH_ATTRIBUTES_SUCCESS,
          // first replace the potential Project related labels to the system configured label
          payload: advancedSearchAttributes.map(parseSearchAttributeAndReplaceProjectTermWithSystemSetting),
        });
        return advancedSearchAttributes;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: ADVANCED_SEARCH_CONFIG_FAILURE,
          alertTitle: "Fetching of Advanced Search Configuration Failed",
        }),
      );
  };
}

export function loadSearchScopeOptions(searchTerm: string, restrictionType: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SearchScope[]> => {
    dispatch({ type: ADVANCED_SEARCH_SCOPE_OPTIONS_REQUEST, payload: searchTerm });

    return webApiClient
      .fetchSearchScopeOptions(searchTerm, restrictionType)
      .then((response: SearchScope[]) => {
        dispatch({
          type: ADVANCED_SEARCH_SCOPE_OPTIONS_SUCCESS,
          payload: searchTerm,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: ADVANCED_SEARCH_SCOPE_OPTIONS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function fetchAdvancedSearchPreview(
  searchType: string,
  searchSubTypeId: number,
  quickSearchQuery: string,
  filterConstraint: IRsqlFilterTree,
  scopeRestriction: SearchScope[],
) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<AdvancedSearchPreview> => {
    const attributesQueryExpression: string = encodeFilter(filterConstraint);

    const requestId: number = Math.floor(Math.random() * 1000);

    // RestrictionType must not be null, therefore we simply set it to genome if no restrictions are in place.
    // On the other hand, restrictionType must NOT be set when searching for genomes. We provide an empty string then.
    const restrictionType: string =
      scopeRestriction && scopeRestriction.length > 0
        ? scopeRestriction[0].restrictionType
        : searchType === ENTITY_TYPE.ORGANISM
          ? ""
          : SEARCH_RESTRICTION_TYPES.GENOME;

    const advancedSearchInput: InputAdvancedSearchInput = {
      id: requestId,
      searchType,
      searchSubTypeId,
      restrictionType: restrictionType,
      restrictions: JSON.stringify(scopeRestriction || undefined),
      quickSearchQuery: quickSearchQuery || undefined,
      attributesQueryExpression: attributesQueryExpression || undefined, // undefined allows to remove the property when stringifying to json
      // escape single quote for gql
    };

    // if the search is exactly the same as last search we resolve an artificial Promise
    const advancedSearchState: IAdvancedSearchState = getState().advancedSearch;
    if (
      advancedSearchState.lastSearch &&
      advancedSearchState.lastSearch.quickSearchQuery === advancedSearchInput.quickSearchQuery &&
      advancedSearchState.lastSearch.attributesQueryExpression === advancedSearchInput.attributesQueryExpression &&
      (advancedSearchState.lastSearch.restrictions || []).length === (advancedSearchInput.restrictions || []).length
    ) {
      // if an async error exists we don't want to clear it, so we reject with the same error
      // (we should use getFormAsyncErrors(ADVANCED_SEARCH_FORM)(getState()) but for some reason it does not work)
      const asyncFormError: string = getState().form[ADVANCED_SEARCH_FORM].error;
      return asyncFormError ? Promise.reject(Error(asyncFormError)) : Promise.resolve(null);
    }

    dispatch({ type: ADVANCED_SEARCH_PREVIEW_REQUEST, payload: advancedSearchInput });

    // remember the searchtype in the session params
    dispatch(
      updateUserParams({ lastAdvancedSearchType: searchType, lastAdvancedSearchSubTypeId: searchSubTypeId || -1 }),
    );

    return webApiClient.fetchAdvancedSearchPreview(advancedSearchInput).then(
      (response: AdvancedSearchPreview) => {
        dispatch({
          type: ADVANCED_SEARCH_PREVIEW_SUCCESS,
          payload: response,
        });
        return response;
      },
      (error: Error) => {
        dispatch({
          type: ADVANCED_SEARCH_PREVIEW_FAILURE,
          payload: requestId,
        });
        throw error;
      },
    );
  };
}

/**
 * Utils
 */

export function createInitialValues(
  searchTypes: SearchType[],
  searchAttributes: SearchAttribute[],
  initialParams: AdvancedSearchParams,
  selectedSearchType: SearchType,
) {
  if (!searchTypes) {
    return {};
  }
  const initialSearchType: SearchType =
    initialParams &&
    searchTypes?.find(
      (st: SearchType) => st.entityType === initialParams.searchType && st.subTypeId == initialParams.searchSubTypeId,
    );
  const initialRestrictionType: ISelectOption =
    initialParams && initialParams.restrictionType
      ? restrictionTypeOptions[initialParams.restrictionType]
      : restrictionTypeOptions[SEARCH_RESTRICTION_TYPES.GENOME];
  let init: any = {
    // init the search type to either the provided initial value or the the first option of the config list
    [AdvancedSearch.FIELD_ADV_SEARCH_TYPE]: initialSearchType || selectedSearchType || searchTypes[0],
    [AdvancedSearch.FIELD_ADV_SEARCH_NAMEORDESCRIPTION]: initialParams && initialParams.quickSearchQuery,
    [AdvancedSearch.FIELD_ADV_SEARCH_RESTRICTION_TYPE]: initialRestrictionType,
    [AdvancedSearch.FIELD_ADV_SEARCH_SCOPE]: initialParams && initialParams.restrictions,
  };

  // init all search attributes with default operators/values
  Object.assign(init, initAllSearchAttributes(searchAttributes));

  // prepopulate the search attributes if any initial query expression is given
  if (initialParams && initialParams.attributesQueryExpression) {
    // we decode the filter expression into filter constraint object...
    const filterConstraints: RsqlFilterConstraint = parse(initialParams.attributesQueryExpression);
    if (filterConstraints) {
      // search operations of the query expression may not be supported by the advanced search dialog
      // ...having a single or multiple fitler units
      const filters: RsqlFilterConstraint[] = isOperationUnit(filterConstraints)
        ? [filterConstraints]
        : (filterConstraints as IRsqlFilterTree)[CONSTRAINT_OPERATORS.AND];
      // we can safely assume that all filter constraints are simple operation units, and we map each filter to the original search attribute
      filters.forEach((filter: IRsqlComparisonExpr) => {
        // use a deep set to create automatically a hierarchy in the init object
        // the filter.key might of the form attribute12345.numberValue, which needs to be interpreted as a path
        init = set(lensPath(filter.key.split(".")), filter, init);
        if (filter.key === SAMPLE_FIELDS.SAMPLE_TYPE) {
          init = set(
            lensProp(AdvancedSearch.FIELD_ADV_SEARCH_SAMPLE_TYPE),
            (filter.value as string[]).map((sampleType: string) => ({ name: sampleType })),
            init,
          );
        }
      });
    }
  }

  return init;
}

export function initAllSearchAttributes(searchAttributes: SearchAttribute[]) {
  let init: any = {};

  // init each of the additional search fields with first operator of the list
  searchAttributes.forEach((searchField: SearchAttribute) => {
    const initialFilterOperation: IRsqlComparisonExpr = {
      key: searchField.field,
      operator: searchField.operators[0].key,
      value: undefined,
    };
    if (searchField.cellType === CELL_TYPE.DATE) {
      initialFilterOperation.value = parseDateToISO(new Date().toISOString()); // this method makes sure the date is the local date (search should not be TZ aware)
      initialFilterOperation.value2 = parseDateToISO(new Date().toISOString());
    }
    if (searchField.cellType === CELL_TYPE.DATE_TIME) {
      // for some reason this freezes the UI, the init gets into an infinite loop !!
      // TODO the component re-init should happen in a different way, using redux-form's initialize or set
      //initialFilterOperation.value = new Date().toISOString();
      //initialFilterOperation.value2 = new Date().toISOString();
    }
    if (searchField.cellType === CELL_TYPE.BOOLEAN) {
      initialFilterOperation.value = "true";
    }
    // use a deep set to create automatically a hierarchy in the init object
    // the searchField.field might of the form attribute12345.numberValue, which needs to be interpreted as a path
    init = set(lensPath(searchField.field!.split(".")), initialFilterOperation, init);
  });
  return init;
}

export function getInitialSearchAttributes(
  searchAttributes: SearchAttribute[],
  initialParams: AdvancedSearchParams,
): SearchAttribute[] {
  if (!initialParams?.attributesQueryExpression) {
    return [];
  }
  // from the provided initial search type we can infer the list of available search attributes we will pick from
  const configSearchAttributes: SearchAttribute[] = searchAttributes;
  // we decode the filter expression into filter constraint object...
  const filterConstraints: RsqlFilterConstraint = parse(initialParams.attributesQueryExpression);
  // ...having a single or multiple fitler units
  const filters: RsqlFilterConstraint[] = isOperationUnit(filterConstraints)
    ? [filterConstraints]
    : (filterConstraints as IRsqlFilterTree)[CONSTRAINT_OPERATORS.AND];
  // we can safely assume that all filter constraints are simple operation units, and we map each filter to the original search attribute
  // we filter out the undefined ones e.g. sample type is a main filter that is dynamically converted from a standard attribute to a static search attribute
  return filters
    .map((filter: IRsqlComparisonExpr) =>
      configSearchAttributes.find((searchAttribute: SearchAttribute) => searchAttribute.field === filter.key),
    )
    .filter((searchAttribute: SearchAttribute) => !!searchAttribute);
}
